/**
 * B2B-Großhandelsanfrage – zentrales Datenmodul.
 *
 * Vollständig isoliert vom bestehenden B2C-Warenkorb (cart-drawer.js,
 * component-cart-items.js, /cart/*-Endpunkte). Verwendet einen eigenen
 * Local-Storage-Namespace und ruft an keiner Stelle Warenkorb-Endpunkte auf.
 *
 * Enthält KEINE Preise, KEINE Lagerbestände, KEINE personenbezogenen Daten.
 * Lediglich: Produkt-/Varianten-ID, Titel, SKU, Menge.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'km_b2b_request_list_v1';
  var MAX_QUANTITY = 1000000;
  var READY_EVENT = 'B2BRequestListReady';
  var UPDATED_EVENT = 'b2b:list-updated';

  /* ---------------------------------------------------------------------
   * Interne Hilfsfunktionen
   * ------------------------------------------------------------------- */

  function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  function isValidPositiveInteger(value) {
    return typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= MAX_QUANTITY;
  }

  function sanitizeString(value, maxLength) {
    if (typeof value !== 'string') return '';
    var trimmed = value.trim();
    if (maxLength && trimmed.length > maxLength) {
      trimmed = trimmed.slice(0, maxLength);
    }
    return trimmed;
  }

  /**
   * Prüft einen einzelnen gespeicherten Eintrag auf Plausibilität.
   * Beschädigte / manipulierte Einträge werden verworfen statt verarbeitet.
   */
  function isValidEntry(entry) {
    if (!isPlainObject(entry)) return false;
    if (typeof entry.productId !== 'string' || entry.productId.length === 0) return false;
    if (entry.variantId !== null && typeof entry.variantId !== 'string') return false;
    if (typeof entry.productTitle !== 'string') return false;
    if (entry.variantTitle !== null && typeof entry.variantTitle !== 'string') return false;
    if (entry.sku !== null && typeof entry.sku !== 'string') return false;
    if (!isValidPositiveInteger(entry.quantity)) return false;
    return true;
  }

  function keyFor(productId, variantId) {
    return String(productId) + '::' + (variantId ? String(variantId) : 'default');
  }

  /* ---------------------------------------------------------------------
   * Persistenz
   * ------------------------------------------------------------------- */

  function readRawList() {
    var raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // Local Storage nicht verfügbar (z. B. privater Modus) – leere Liste zurückgeben.
      return [];
    }
    if (!raw) return [];

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      // Beschädigtes JSON – verwerfen, nicht verarbeiten.
      return [];
    }

    if (!Array.isArray(parsed)) return [];

    // Jeden Eintrag einzeln validieren; ungültige Einträge werden stillschweigend entfernt.
    return parsed.filter(isValidEntry);
  }

  function writeRawList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Speichern fehlgeschlagen (z. B. Speicherlimit) – Funktion bricht kontrolliert ab,
      // keine Preise/Bestände sind betroffen, es handelt sich nur um die Anfrageliste.
      return false;
    }
    return true;
  }

  function notifyUpdated() {
    var list = getList();
    var totalPositions = list.length;
    var totalQuantity = list.reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);
    try {
      window.dispatchEvent(
        new CustomEvent(UPDATED_EVENT, {
          detail: { totalPositions: totalPositions, totalQuantity: totalQuantity, list: list }
        })
      );
    } catch (e) {
      /* Custom Events werden von allen unterstützten Browsern bereitgestellt; defensive no-op. */
    }
  }

  /* ---------------------------------------------------------------------
   * Öffentliche API
   * ------------------------------------------------------------------- */

  function getList() {
    return readRawList();
  }

  function getTotals() {
    var list = getList();
    return {
      totalPositions: list.length,
      totalQuantity: list.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0)
    };
  }

  /**
   * Fügt eine Position hinzu oder aktualisiert die Menge einer bestehenden Position.
   * Schlüssel: productId + variantId (Varianten werden eindeutig getrennt).
   */
  function addOrUpdateItem(item) {
    if (!isPlainObject(item)) return false;

    var productId = sanitizeString(item.productId, 100);
    var variantId = item.variantId ? sanitizeString(item.variantId, 100) : null;
    var productTitle = sanitizeString(item.productTitle, 300);
    var variantTitle = item.variantTitle ? sanitizeString(item.variantTitle, 300) : null;
    var sku = item.sku ? sanitizeString(item.sku, 200) : null;
    var quantity = parseInt(item.quantity, 10);

    if (!productId || !isValidPositiveInteger(quantity)) return false;

    var list = getList();
    var targetKey = keyFor(productId, variantId);
    var existingIndex = -1;

    for (var i = 0; i < list.length; i++) {
      if (keyFor(list[i].productId, list[i].variantId) === targetKey) {
        existingIndex = i;
        break;
      }
    }

    var entry = {
      productId: productId,
      variantId: variantId,
      productTitle: productTitle,
      variantTitle: variantTitle,
      sku: sku,
      quantity: quantity
    };

    if (existingIndex > -1) {
      // Bestehende Position (gleiches Produkt + gleiche Variante) wird aktualisiert,
      // NICHT eine andere Variante desselben Produkts überschrieben.
      list[existingIndex] = entry;
    } else {
      list.push(entry);
    }

    writeRawList(list);
    notifyUpdated();
    return true;
  }

  function updateQuantity(productId, variantId, quantity) {
    var qty = parseInt(quantity, 10);
    if (!isValidPositiveInteger(qty)) return false;

    var list = getList();
    var targetKey = keyFor(productId, variantId);
    var found = false;

    for (var i = 0; i < list.length; i++) {
      if (keyFor(list[i].productId, list[i].variantId) === targetKey) {
        list[i].quantity = qty;
        found = true;
        break;
      }
    }

    if (!found) return false;

    writeRawList(list);
    notifyUpdated();
    return true;
  }

  function removeItem(productId, variantId) {
    var list = getList();
    var targetKey = keyFor(productId, variantId);
    var filtered = list.filter(function (item) {
      return keyFor(item.productId, item.variantId) !== targetKey;
    });

    if (filtered.length === list.length) return false;

    writeRawList(filtered);
    notifyUpdated();
    return true;
  }

  function clearList() {
    writeRawList([]);
    notifyUpdated();
  }

  /**
   * Generiert eine eindeutige Anfrage-Nummer nach dem Schema
   * B2B-JJJJMMTT-HHMM-XXXX mit zufälligem, alphanumerischem Suffix.
   */
  function generateRequestId() {
    var now = new Date();
    var pad = function (n) {
      return String(n).padStart(2, '0');
    };
    var datePart =
      now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate());
    var timePart = pad(now.getHours()) + pad(now.getMinutes());

    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // ohne verwechselbare Zeichen (0/O, 1/I)
    var suffix = '';
    var randomValues;

    if (window.crypto && window.crypto.getRandomValues) {
      randomValues = new Uint32Array(4);
      window.crypto.getRandomValues(randomValues);
      for (var i = 0; i < 4; i++) {
        suffix += chars[randomValues[i] % chars.length];
      }
    } else {
      for (var j = 0; j < 4; j++) {
        suffix += chars[Math.floor(Math.random() * chars.length)];
      }
    }

    return 'B2B-' + datePart + '-' + timePart + '-' + suffix;
  }

  /**
   * Formatiert die vollständige, für den Betreiber lesbare Anfrage als Klartext
   * (wird in contact[body] übertragen, da kein separates Betreff-Feld existiert).
   */
  function formatRequestBody(contactData, requestId, list) {
    var now = new Date();
    var pad = function (n) {
      return String(n).padStart(2, '0');
    };
    var receivedAt =
      pad(now.getDate()) + '.' + pad(now.getMonth() + 1) + '.' + now.getFullYear() +
      ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes());

    var lines = [];
    lines.push('NEUE B2B-GROSSHANDELSANFRAGE');
    lines.push('Eingegangen am: ' + receivedAt);
    lines.push('Anfrage-Nr.: ' + requestId);
    lines.push('');
    lines.push('KONTAKTDATEN');
    lines.push('Firma: ' + (contactData.company || '-'));
    lines.push('Ansprechpartner: ' + (contactData.contactName || '-'));
    lines.push('E-Mail: ' + (contactData.email || '-'));
    lines.push('Telefon: ' + (contactData.phone || '-'));
    lines.push('Land: ' + (contactData.country || '-'));
    lines.push('PLZ/Ort: ' + (contactData.zipCity || '-'));
    lines.push('USt-ID: ' + (contactData.vatId || '-'));
    lines.push('Kundentyp: ' + (contactData.customerType || '-'));
    lines.push('');
    lines.push('ANGEFRAGTE PRODUKTE');

    var totalQuantity = 0;
    list.forEach(function (item, index) {
      totalQuantity += item.quantity;
      lines.push(
        (index + 1) + '. Produkt: ' + item.productTitle +
        ' | Variante: ' + (item.variantTitle || '-') +
        ' | SKU: ' + (item.sku || '-') +
        ' | Menge: ' + item.quantity
      );
    });

    lines.push('');
    lines.push('Positionen gesamt: ' + list.length);
    lines.push('Stückzahl gesamt: ' + totalQuantity);
    lines.push('');
    lines.push('NACHRICHT DES KUNDEN:');
    lines.push(contactData.message ? contactData.message : '(keine Angabe)');
    lines.push('');
    lines.push('---');
    lines.push(
      'Dies ist eine unverbindliche Großhandelsanfrage. Es entsteht noch kein Kaufvertrag. ' +
      'Nach Prüfung erhalten Sie von uns ein individuelles Angebot.'
    );

    return lines.join('\n');
  }

  /* ---------------------------------------------------------------------
   * Übermittlung – natives Shopify-Kontaktformular per fetch, mit
   * inhaltlicher Erfolgsprüfung und automatischem klassischem Fallback.
   * ------------------------------------------------------------------- */

  /**
   * @param {HTMLFormElement} formEl - das Formular-Element (b2b-request-form.liquid)
   * @param {Object} callbacks - { onSuccess(requestId), onFallback(), onError(message) }
   */
  function submitRequest(formEl, callbacks) {
    callbacks = callbacks || {};
    var successMarkerSelector = '[data-b2b-form-success]';

    var formData = new FormData(formEl);
    var body = new URLSearchParams();
    formData.forEach(function (value, key) {
      body.append(key, value);
    });

    fetch(formEl.action, {
      method: 'POST',
      body: body.toString(),
      redirect: 'follow',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'text/html, */*; q=0.01',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (response) {
        // Niemals allein anhand von HTTP 200 auf Erfolg schließen: Umleitung zu einer
        // Challenge-/Captcha-Seite oder unerwarteter Inhalt müssen erkannt werden.
        if (!response.ok) {
          throw new Error('non-ok-status');
        }
        if (response.url && /\/challenge/.test(response.url)) {
          throw new Error('challenge-redirect');
        }
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        if (doc.querySelector(successMarkerSelector)) {
          if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess();
          return;
        }

        // Kein eindeutiger Erfolgsmarker gefunden (z. B. Formularfehler, unerwarteter
        // Seiteninhalt, Captcha-Herausforderung) -> klassischer Fallback statt
        // fälschlicher Erfolgsmeldung.
        if (typeof callbacks.onFallback === 'function') callbacks.onFallback();
      })
      .catch(function () {
        if (typeof callbacks.onFallback === 'function') callbacks.onFallback();
      });
  }

  /* ---------------------------------------------------------------------
   * Spam-Schutz: Mindestfüllzeit
   * ------------------------------------------------------------------- */

  function isSubmittedTooFast(formLoadedAt, minMillis) {
    minMillis = minMillis || 3000;
    return Date.now() - formLoadedAt < minMillis;
  }

  /* ---------------------------------------------------------------------
   * Export
   * ------------------------------------------------------------------- */

  window.B2BRequestList = {
    getList: getList,
    getTotals: getTotals,
    addOrUpdateItem: addOrUpdateItem,
    updateQuantity: updateQuantity,
    removeItem: removeItem,
    clearList: clearList,
    generateRequestId: generateRequestId,
    formatRequestBody: formatRequestBody,
    submitRequest: submitRequest,
    isSubmittedTooFast: isSubmittedTooFast,
    UPDATED_EVENT: UPDATED_EVENT
  };

  try {
    window.dispatchEvent(new CustomEvent(READY_EVENT));
  } catch (e) {
    /* no-op */
  }
})();
